import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError, api, errorMessage, formatCell, type IcebergField, type QueryResult, type TableDocumentation, type TableMetadata } from '../api'
import CatalogSelect, { useCatalogs } from '../components/CatalogSelect'
import ErrorBox from '../components/ErrorBox'
import TableAbout, { type DocsState } from '../components/TableAbout'
import { formatTime, sqlTableRef, rowsLabel } from '../format'
import { isAdmin } from '../roles'

type Sel = { ns: string; tbl: string }

/** A 403 says no: the person's grants do not change by asking again. */
function forbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

export default function Catalog() {
  const [params] = useSearchParams()
  // The catalog the person chose. Absent means the API's default, which is what every
  // request did before there were several catalogs.
  const catalog = params.get('catalog') ?? undefined
  const [selected, setSelected] = useState<Sel | null>(null)
  const [filter, setFilter] = useState(params.get('q') ?? '')

  // `?catalog=` and the two selection params arrive in one history entry, so the selection
  // is read from the URL rather than kept beside it: switching catalogs clears it.
  useEffect(() => {
    const ns = params.get('ns')
    const tbl = params.get('tbl')
    setSelected(ns && tbl ? { ns, tbl } : null)
  }, [params])

  useEffect(() => {
    const q = params.get('q')
    if (q) setFilter(q)
  }, [params])

  const catalogs = useCatalogs()
  // No catalog is open to this person: the API's own sentence, and no Retry — the list
  // can only change when an administrator grants a catalog (stardelt-nova#234).
  const denied = catalogs.isError && forbidden(catalogs.error)

  const namespaces = useQuery({
    queryKey: ['catalog', 'namespaces', catalog],
    queryFn: () => api.namespaces(catalog),
    enabled: !denied,
  })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-5 h-full min-h-0">
      {/* A page has one h1: with a table open, that table's name is the heading below. */}
      {!selected && <h1 className="sr-only">Catalog</h1>}
      <aside className="card p-3 flex flex-col min-h-0">
        <div className="px-1 pb-2">
          <div className="label mb-1.5">Catalog</div>
          <CatalogSelect className="mb-2" />
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter tables…"
            aria-label="Filter tables"
            className="input py-1.5"
          />
        </div>
        <div className="overflow-auto flex-1">
          {denied ? (
            <div className="p-2"><ErrorBox error={catalogs.error} /></div>
          ) : (
            <>
              {namespaces.isLoading && <div className="text-text-mute text-sm p-2">loading…</div>}
              {namespaces.isError && (
                <div className="p-2">
                  <ErrorBox
                    error={namespaces.error}
                    onRetry={forbidden(namespaces.error) ? undefined : () => void namespaces.refetch()}
                  />
                </div>
              )}
              {namespaces.data && namespaces.data.namespaces.length === 0 && (
                <p className="text-text-mute text-sm p-2">This catalog has no schemas yet.</p>
              )}
              <ul className="space-y-1">
                {namespaces.data?.namespaces.map(parts => {
                  const ns = parts.join('.')
                  return (
                    <NamespaceItem
                      key={ns}
                      ns={ns}
                      catalog={catalog}
                      filter={filter}
                      onSelect={setSelected}
                      selected={selected}
                    />
                  )
                })}
              </ul>
            </>
          )}
        </div>
      </aside>

      <section className="min-w-0 min-h-0 overflow-auto">
        {selected ? (
          <TableDetail ns={selected.ns} tbl={selected.tbl} catalog={catalog} />
        ) : (
          <div className="card p-8 h-full grid place-items-center text-center">
            <div>
              <div className="text-text-dim">Select a table to inspect it.</div>
              <div className="text-text-mute text-sm mt-1">Or load a file to create one.</div>
              <Link to="/data" className="btn-ghost mt-4">Load data</Link>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

function NamespaceItem({ ns, catalog, filter, onSelect, selected }: {
  ns: string
  catalog?: string
  filter: string
  onSelect: (sel: Sel) => void
  selected: Sel | null
}) {
  const [open, setOpen] = useState(true)
  const tables = useQuery({
    queryKey: ['catalog', 'tables', catalog, ns],
    queryFn: () => api.tables(ns, catalog),
    enabled: open,
  })

  const matches = (name: string) => {
    if (!filter.trim()) return true
    return `${ns}.${name}`.toLowerCase().includes(filter.toLowerCase())
  }
  const visible = (tables.data?.identifiers ?? []).filter(id => matches(id.name))

  if (filter.trim() && visible.length === 0) return null

  return (
    <li>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-[8px] hover:bg-bg-soft/40">
        <span className="text-text-mute text-xs">{open ? '▾' : '▸'}</span>
        <span className="text-sm font-medium">{ns}</span>
        <span className="ml-auto text-[11px] text-text-mute">{visible.length}</span>
      </button>
      {open && (
        <ul className="ml-3 mt-0.5 space-y-0.5">
          {tables.isLoading && <li className="text-text-mute text-xs px-2 py-1">loading…</li>}
          {tables.isError && !tables.data && <li className="px-2 py-1"><ErrorBox error={tables.error} onRetry={() => tables.refetch()} /></li>}
          {visible.map(id => {
            const sel = selected?.ns === ns && selected?.tbl === id.name
            return (
              <li key={id.name}>
                <button
                  onClick={() => onSelect({ ns, tbl: id.name })}
                  aria-current={sel ? 'true' : undefined}
                  className={[
                    'w-full text-left px-2 py-1.5 rounded-[8px] text-sm flex items-center gap-2',
                    sel ? 'bg-accent/15 text-text border border-accent/30' : 'text-text-dim hover:text-text hover:bg-bg-soft/40 border border-transparent',
                  ].join(' ')}
                >
                  <span className="text-text-mute">◇</span>
                  <span className="truncate">{id.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

function TableDetail({ ns, tbl, catalog }: Sel & { catalog?: string }) {
  const [tab, setTab] = useState<'preview' | 'columns' | 'details' | 'history'>('preview')
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const meta = useQuery({
    queryKey: ['catalog', 'table', catalog, ns, tbl],
    queryFn: () => api.tableMeta(ns, tbl, catalog),
  })
  const docs = useQuery({
    queryKey: ['catalog', 'table-docs', catalog, ns, tbl],
    queryFn: () => api.tableDocs(ns, tbl, catalog),
  })
  const preview = useQuery({
    queryKey: ['preview', catalog, ns, tbl],
    queryFn: () => api.query(`SELECT * FROM ${sqlTableRef(ns, tbl, catalog)} LIMIT 50`),
    enabled: tab === 'preview',
  })

  /** A write answers the fresh documentation; it replaces the read's data for both halves. */
  const onDocsSaved = (answer: TableDocumentation) => {
    qc.setQueryData(['catalog', 'table-docs', catalog, ns, tbl], answer)
    // The Details tab shows the same catalog properties; let it read them again.
    void qc.invalidateQueries({ queryKey: ['catalog', 'table', catalog, ns, tbl] })
  }

  /** The editor opens the table in the catalog it was browsed in, not the default one. */
  const queryHref = (sql: string) => {
    const query = new URLSearchParams({ sql })
    if (catalog) query.set('catalog', catalog)
    return `/query?${query.toString()}`
  }

  const m = meta.data?.metadata
  const schemaId = m?.['current-schema-id'] ?? m?.schemas[0]?.['schema-id'] ?? 0
  const schema = m?.schemas.find(s => s['schema-id'] === schemaId) ?? m?.schemas[0]
  const partSpec = m?.['partition-specs']?.[0]

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-text-mute">{ns}</div>
          <h1 className="text-2xl font-bold tracking-tight font-mono">{tbl}</h1>
          {m && (
            <div className="text-xs text-text-mute mt-1 font-mono truncate max-w-[620px]">{m.location}</div>
          )}
        </div>
        <div className="flex gap-2">
          <Link to={queryHref(`SELECT * FROM ${sqlTableRef(ns, tbl, catalog)} LIMIT 100`)} className="btn-primary">
            Query table
          </Link>
          <Link to="/data" className="btn-ghost">Load data</Link>
        </div>
      </header>

      <TableAbout ns={ns} tbl={tbl} catalog={catalog} me={me.data} docs={docs} onSaved={onDocsSaved} />

      <div className="flex gap-2">
        {(['preview', 'columns', 'details', 'history'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className={[
              'px-3 py-1.5 rounded-[8px] text-sm border capitalize',
              tab === t
                ? 'bg-accent/15 border-accent/30 text-text'
                : 'border-border text-text-dim hover:text-text',
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {meta.isError && <ErrorBox error={meta.error} onRetry={() => meta.refetch()} />}

      {tab === 'preview' && <Preview q={preview} />}

      {tab === 'columns' && (
        <ColumnsTab
          ns={ns}
          tbl={tbl}
          catalog={catalog}
          fields={schema?.fields}
          docs={docs}
          onSaved={onDocsSaved}
        />
      )}

      {tab === 'details' && meta.data && <Details meta={meta.data} partition={partSpec} />}

      {tab === 'history' && <HistoryTab ns={ns} tbl={tbl} catalog={catalog} />}
    </div>
  )
}

function ColumnsTab({ ns, tbl, catalog, fields, docs, onSaved }: {
  ns: string
  tbl: string
  catalog?: string
  fields?: IcebergField[]
  docs: DocsState
  onSaved: (doc: TableDocumentation) => void
}) {
  const canEdit = docs.data?.canEdit === true
  const descriptions = new Map((docs.data?.columns ?? []).map(c => [c.name, c.description]))
  const [editing, setEditing] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const area = useRef<HTMLTextAreaElement>(null)
  const editButtons = useRef(new Map<string, HTMLButtonElement>())
  const pendingFocus = useRef<string | null>(null)

  useEffect(() => {
    if (editing !== null) {
      area.current?.focus()
    } else if (pendingFocus.current !== null) {
      editButtons.current.get(pendingFocus.current)?.focus()
      pendingFocus.current = null
    }
  }, [editing])

  function open(name: string) {
    setEditing(name)
    setText(descriptions.get(name) ?? '')
    setError(null)
    setNotice(null)
  }

  function close() {
    if (editing !== null) pendingFocus.current = editing
    setEditing(null)
    setError(null)
  }

  async function save() {
    if (editing === null) return
    const next = text.trim() === '' ? null : text.trim()
    if (next === (descriptions.get(editing) ?? null)) {
      close()
      return
    }
    setSaving(editing)
    setError(null)
    try {
      const answer = await api.putColumnDoc(ns, tbl, editing, next, catalog)
      onSaved(answer)
      setNotice('Saved.')
      pendingFocus.current = editing
      setEditing(null)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="card overflow-hidden min-w-0">
      {notice && <p role="status" className="border-b border-border px-4 py-2 text-sm text-ok break-words">{notice}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-soft/50">
            <tr>
              <th className="th px-4 py-2.5">#</th>
              <th className="th px-4 py-2.5">Column</th>
              <th className="th px-4 py-2.5">Type</th>
              <th className="th px-4 py-2.5">Required</th>
              <th className="th px-4 py-2.5">Description</th>
            </tr>
          </thead>
          <tbody>
            {fields?.map(f => (
              <tr key={f.id} className="border-t border-border/70">
                <td className="td px-4 text-text-mute font-mono">{f.id}</td>
                <td className="td px-4 font-mono break-words">{f.name}</td>
                <td className="td px-4 text-text-dim font-mono break-words">{typeof f.type === 'string' ? f.type : JSON.stringify(f.type)}</td>
                <td className="td px-4 text-text-mute">{f.required ? 'yes' : '—'}</td>
                <td className="td px-4 text-text-dim max-w-[32rem]">
                  {editing === f.name ? (
                    <div
                      className="min-w-[15rem] space-y-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault()
                          close()
                        }
                      }}
                    >
                      <textarea
                        ref={area}
                        aria-label={`Description of ${f.name}`}
                        className="input min-h-[4rem] resize-y"
                        maxLength={4000}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                      />
                      <div className="text-right text-[11px] text-text-mute">{text.length} / 4000</div>
                      {error && <p role="alert" className="text-xs text-bad whitespace-pre-wrap break-words">{error}</p>}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="btn-primary py-1 px-2 text-xs"
                          aria-label={`Save description of ${f.name}`}
                          disabled={saving === f.name}
                          onClick={() => void save()}
                        >
                          {saving === f.name ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost py-1 px-2 text-xs"
                          aria-label={`Cancel description of ${f.name}`}
                          onClick={close}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-w-0 items-start gap-2">
                      <span className="min-w-0 flex-1 break-words">{descriptions.get(f.name) ?? '—'}</span>
                      {canEdit && (
                        <button
                          type="button"
                          ref={(el) => {
                            if (el) editButtons.current.set(f.name, el)
                            else editButtons.current.delete(f.name)
                          }}
                          aria-label={`Edit description of ${f.name}`}
                          className="btn-ghost shrink-0 py-0.5 px-2 text-xs"
                          onClick={() => open(f.name)}
                        >
                          <span aria-hidden="true">✎</span> Edit
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!fields && <tr><td className="px-4 py-5 text-text-mute" colSpan={5}>Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}


function Preview({ q }: { q: { data?: QueryResult; isLoading: boolean; error: unknown; refetch: () => void } }) {
  if (q.isLoading) return <div className="card p-6 text-text-mute text-sm">loading preview…</div>
  if (q.error) return <div className="card p-6"><ErrorBox error={q.error} onRetry={() => q.refetch()} /></div>
  if (!q.data) return null
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2 text-xs text-text-mute border-b border-border flex justify-between">
        <span>{rowsLabel(q.data.row_count)} · preview</span>
        <span className="font-mono">{q.data.query_id}</span>
      </div>
      <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-sm font-mono">
          <thead className="bg-bg-soft/60 sticky top-0">
            <tr>{q.data.columns.map((c, i) => <th key={i} className="th px-4 py-2">{c.name}</th>)}</tr>
          </thead>
          <tbody>
            {q.data.rows.map((row, i) => (
              <tr key={i} className="border-t border-border/60">
                {row.map((cell, j) => <td key={j} className="td px-4 text-text-dim">{formatCell(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function HistoryTab({ ns, tbl, catalog }: Sel & { catalog?: string }) {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const history = useQuery({
    queryKey: ['catalog', 'history', catalog, ns, tbl],
    queryFn: () => api.tableHistory(ns, tbl, catalog),
  })
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [rollbackTarget, setRollbackTarget] = useState<string | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [rollbackOk, setRollbackOk] = useState<string | null>(null)

  const preview = useQuery({
    queryKey: ['history-preview', catalog, ns, tbl, previewId],
    queryFn: () =>
      api.query(`SELECT * FROM ${sqlTableRef(ns, tbl, catalog)} FOR VERSION AS OF ${previewId} LIMIT 100`),
    enabled: previewId !== null,
  })

  const rollback = useMutation({
    mutationFn: (snapshotId: string) => api.rollback(ns, tbl, snapshotId, catalog),
    onSuccess: (_r, snapshotId) => {
      setRollbackOk(`Rolled back to snapshot ${snapshotId}.`)
      setRollbackTarget(null)
      setConfirmText('')
      qc.invalidateQueries({ queryKey: ['catalog', 'history', catalog, ns, tbl] })
      qc.invalidateQueries({ queryKey: ['preview', catalog, ns, tbl] })
      if (previewId !== null) qc.invalidateQueries({ queryKey: ['history-preview', catalog, ns, tbl, previewId] })
    },
  })

  const docs = useQuery({
    queryKey: ['catalog', 'table-docs', catalog, ns, tbl],
    queryFn: () => api.tableDocs(ns, tbl, catalog),
  })
  const admin = isAdmin(me.data)
  const canRollback = docs.data?.canRollback === true
  const rollbackRule = docs.data?.rollbackRule
  const cols = history.data?.columns ?? []
  const idx = (name: string) => cols.findIndex(c => c.name === name)
  const iTime = idx('committed_at')
  const iSnap = idx('snapshot_id')
  const iOp = idx('operation')
  const iSummary = idx('summary')

  if (history.isLoading || docs.isLoading) return <div className="card p-6 text-text-mute text-sm">loading history…</div>
  if (history.isError) return <div className="card p-6"><ErrorBox error={history.error} onRetry={() => history.refetch()} /></div>
  const rows = history.data?.rows ?? []
  if (rows.length === 0) {
    return (
      <div className="space-y-4">
        <div className="card p-6 text-text-mute text-sm">
          No snapshots recorded. {me.data?.demo ? 'Demo mode has no table history.' : 'The table has no snapshots yet.'}
        </div>
        {!canRollback && rollbackRule && (
          <p className="text-text-mute text-xs break-words">{rollbackRule}</p>
        )}
      </div>
    )
  }

  const snapIdOf = (row: unknown[]): string | null => {
    if (iSnap < 0) return null
    const v = row[iSnap]
    // The backend sends ids as strings to keep all 64 bits through JSON.parse;
    // an old backend may still send a safe number. Anything else is unusable.
    if (typeof v === 'string' && /^-?\d+$/.test(v)) return v
    if (typeof v === 'number' && Number.isSafeInteger(v)) return String(v)
    return null
  }

  const summaryText = (row: unknown[]): string => {
    if (iSummary < 0) return '—'
    const v = row[iSummary] as unknown
    if (v === null || v === undefined) return '—'
    if (typeof v === 'object') {
      const o = v as Record<string, unknown>
      // Iceberg snapshot summaries carry row/file counts under various keys;
      // surface the familiar ones when present, else the raw map.
      const picks = ['total-records', 'added-records', 'deleted-records', 'added-data-files', 'deleted-data-files']
        .filter(k => o[k] !== undefined)
        .map(k => `${k}=${String(o[k])}`)
      if (picks.length > 0) return picks.join(' · ')
      return JSON.stringify(o)
    }
    return String(v)
  }

  return (
    <div className="space-y-4">
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-soft/50">
            <tr>
              <th className="th px-4 py-2.5">Time</th>
              <th className="th px-4 py-2.5">Operation</th>
              <th className="th px-4 py-2.5">Snapshot</th>
              <th className="th px-4 py-2.5">Summary</th>
              <th className="th px-4 py-2.5"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const id = snapIdOf(row)
              return (
                <tr key={i} className="border-t border-border/70">
                  <td className="td px-4 font-mono whitespace-nowrap">{iTime >= 0 ? formatCell(row[iTime]) : '—'}</td>
                  <td className="td px-4 font-mono">{iOp >= 0 ? formatCell(row[iOp]) : '—'}</td>
                  <td className="td px-4 font-mono text-text-mute">{id !== null ? String(id) : '—'}</td>
                  <td className="td px-4 font-mono text-text-dim break-all">{summaryText(row)}</td>
                  <td className="td px-4 whitespace-nowrap text-right">
                    {id !== null && (
                      <button
                        onClick={() => setPreviewId(id)}
                        aria-pressed={previewId === id}
                        className="btn-ghost py-1 px-2 text-xs"
                      >
                        Preview
                      </button>
                    )}
                    {(canRollback || admin) && id !== null && (
                      <button
                        onClick={() => { setRollbackTarget(id); setConfirmText(''); setRollbackOk(null); rollback.reset() }}
                        className="btn-ghost py-1 px-2 text-xs text-bad ml-2"
                      >
                        Roll back…
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!canRollback && rollbackRule && (
        <p className="text-text-mute text-xs break-words">{rollbackRule}</p>
      )}

      {rollbackTarget !== null && (
        <div className="card p-5 border-red-500/40" role="dialog" aria-label="Confirm rollback">
          <div className="text-sm font-semibold">Roll back {ns}.{tbl} to snapshot {rollbackTarget}?</div>
          <p className="text-sm text-text-dim mt-1">
            This moves the current pointer to that snapshot. No snapshots or files are deleted,
            and the rollback itself appears in the history.
          </p>
          <label className="block mt-3">
            <span className="label block mb-1.5">Type <span className="font-mono">{tbl}</span> to confirm</span>
            <input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              className="input"
              aria-label={`Type ${tbl} to confirm rollback`}
            />
          </label>
          {rollback.isError && (
            <div className="rounded-[10px] border border-red-500/40 bg-red-500/10 p-3 mt-3 text-sm text-bad font-mono whitespace-pre-wrap break-words">
              {errorMessage(rollback.error)}
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button
              disabled={confirmText !== tbl || rollback.isPending}
              onClick={() => rollback.mutate(rollbackTarget)}
              className="btn-primary disabled:opacity-40"
            >
              {rollback.isPending ? 'Rolling back…' : 'Roll back to this version'}
            </button>
            <button onClick={() => { setRollbackTarget(null); setConfirmText(''); rollback.reset() }} className="btn-ghost">
              Cancel
            </button>
          </div>
        </div>
      )}
      {rollbackOk && <div className="rounded-[10px] border border-emerald-400/30 bg-emerald-400/10 p-3 text-sm text-ok">{rollbackOk}</div>}
      {rollback.isError && rollbackTarget === null && (
        <div className="rounded-[10px] border border-red-500/40 bg-red-500/10 p-3 text-sm text-bad font-mono whitespace-pre-wrap break-words">
          {errorMessage(rollback.error)}
        </div>
      )}

      {previewId !== null && (
        <div className="space-y-2">
          <div className="text-xs text-text-mute font-mono">
            Time travel: <span className="text-text-dim">SELECT * FROM {sqlTableRef(ns, tbl, catalog)} FOR VERSION AS OF {previewId} LIMIT 100</span>
            <button onClick={() => setPreviewId(null)} className="btn-ghost py-0.5 px-2 text-xs ml-3">Close</button>
          </div>
          <Preview q={preview} />
        </div>
      )}
    </div>
  )
}

function Details({ meta, partition }: {
  meta: TableMetadata
  partition?: { fields: Array<{ name: string; transform: string; 'source-id': number }> }
}) {
  const m = meta.metadata
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="card p-5">
        <div className="label mb-3">Table</div>
        <Row k="UUID" v={m['table-uuid']} mono />
        <Row k="Location" v={m.location} mono />
        <Row k="Updated" v={formatTime(m['last-updated-ms'])} />
        <Row k="Metadata" v={meta['metadata-location']} mono />
      </div>
      <div className="card p-5">
        <div className="label mb-3">Partitioning</div>
        {partition && partition.fields.length > 0 ? (
          <ul className="text-sm font-mono space-y-1">
            {partition.fields.map(f => (
              <li key={f.name}>
                <span className="text-accent">{f.transform}</span>
                <span className="text-text-mute"> → </span>{f.name}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-text-mute">Unpartitioned</div>
        )}
        {m.properties && (
          <>
            <div className="label mt-5 mb-2">Properties</div>
            <ul className="text-sm font-mono space-y-1">
              {Object.entries(m.properties).map(([k, v]) => (
                <li key={k}><span className="text-text-dim">{k}</span> = {v}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="py-1.5 border-b border-border/60 last:border-0">
      <div className="text-[11px] uppercase tracking-widest text-text-mute">{k}</div>
      <div className={`text-sm break-all ${mono ? 'font-mono' : ''}`}>{v}</div>
    </div>
  )
}
